'use client'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faAnglesLeft,
  faAnglesRight,
  faChevronLeft,
  faChevronRight,
  faFilter,
  faMagnifyingGlass,
  faPlus,
  faRefresh
} from '@fortawesome/pro-regular-svg-icons'
import {
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  outlinedInputClasses,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table'
import React, { useState, useEffect, useRef } from 'react'
import { Filter } from 'src/components/era-package/filters';
import ChildCard from 'src/components/era-package/shared/ChildCard'
import { useColumns } from './tradepartnerhooks'
import { TradePartnerDetailModal } from '../TradePartnerDetailModal'
import { useServerPagination } from '../../hooks/useServerPagination'
import pathMap from 'src/paths/pathMap'
import AbiTradePartnerApiUrls from '../../../../apiUrls/AbiTradePartnerApiUrls'
import { Link } from 'react-router-dom'

const COLUMN_TO_PARAM = {
  partyMasterName: 'partyMasterName',
  name: 'name',
  partyCode: 'partyCode',
  countryCode: 'countryCode',
  active: 'active',
}

const TRADE_PARTNER_DEFAULT_PARAMS = {
  page: 0,
  size: 10,
  search: '',
  partyMasterName: '',
  partyCode: '',
  name: '',
  partyIdentifier: '',
  city: '',
  countryCode: '',
  contactName: '',
  active: null,
}

// Main Component
const TradePartnerSubmissions = () => {
    const [searchInput, setSearchInput] = useState('')
  const [columnFilters, setColumnFilters] = useState([])
  const [showFilters, setShowFilters] = useState(false)
  const [selectedRow, setSelectedRow] = useState()
  const columns = useColumns()
  const [sorting, setSorting] = useState([{ id: 'updatedDate', desc: true }])
  const debounceRef = useRef(null)

  const { data, totalElements, totalPages, loading, params, fetchPage, updateParams } =
    useServerPagination(AbiTradePartnerApiUrls.LIST, TRADE_PARTNER_DEFAULT_PARAMS)

  

  const handleRowClick = (row) => {
    setSelectedRow(row.original)
  }

    const pageIndex = params.page
  const pageSize = params.size

    const table = useReactTable({
    data,
    columns,
    filterFns: {},
    state: {
      columnFilters,
      sorting,
      pagination: { pageIndex, pageSize },
    },
    manualPagination: true,
    pageCount: totalPages,
    onSortingChange: setSorting,
    onColumnFiltersChange: (updater) => {
      const next = typeof updater === 'function' ? updater(columnFilters) : updater
      setColumnFilters(next)
            const mapped = { partyMasterName: '', name: '', partyCode: '', countryCode: '', active: null }
                  next.forEach(({ id, value }) => {
        const key = COLUMN_TO_PARAM[id]
        if (!key) return
        if (id === 'active') {
          mapped[key] = value === 'all' ? null : value === 'true' || value === true
        } else {
          mapped[key] = value ?? ''
        }
      })
      updateParams({ ...mapped, page: 0 })
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    debugTable: false,
    debugHeaders: false,
    debugColumns: false
  })

    return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Stack spacing={2}>
        {/* Search and Filter Controls */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems='center'
          spacing={2}
          sx={{ mb: 2 }}
        >
          <TextField
            size='small'
            placeholder='Search all columns...'
                                    value={searchInput}
            onChange={(e) => {
              const value = e.target.value
              setSearchInput(value)
              clearTimeout(debounceRef.current)
              debounceRef.current = setTimeout(() => {
                updateParams({ search: value, page: 0 })
              }, 500)
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <FontAwesomeIcon
                    icon={faMagnifyingGlass}
                    style={{ width: 16, height: 16 }}
                  />
                </InputAdornment>
              )
            }}
            sx={{
              maxWidth: { xs: '100%', sm: '300px' },
              [`& .${outlinedInputClasses.notchedOutline}`]: {
                inset: 0
              }
            }}
          />
          <Button
            variant='outlined'
            onClick={() => setShowFilters(!showFilters)}
            startIcon={
              <FontAwesomeIcon
                icon={faFilter}
                style={{ width: 16, height: 16 }}
              />
            }
          >
            {showFilters ? 'Hide Filters' : 'Show Column Filters'}
          </Button>
          <Button
            component={Link}
            to={pathMap.ABI_TRADE_PARTNER_ADD_VIEW}
            variant='outlined'
            startIcon={
              <FontAwesomeIcon
                icon={faPlus}
                style={{ width: 16, height: 16 }}
              />
            }
          >
            Add Trade Partner
          </Button>
        </Stack>
        <ChildCard sx={{ p: 0, mb: -3 }}>
          <Stack>
            <TableContainer>
              <Table>
                <TableHead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <React.Fragment key={headerGroup.id}>
                      <TableRow>
                        {headerGroup.headers.map((header) => (
                          <TableCell key={header.id}>
                            <Typography
                              variant='body'
                              color={'primary.main'}
                              fontWeight='600'
                              {...{
                                className: header.column.getCanSort()
                                  ? 'cursor-pointer select-none'
                                  : '',
                                onClick: header.column.getToggleSortingHandler()
                              }}
                            >
                              {header.isPlaceholder
                                ? null
                                : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                  )}
                              {{
                                asc: ' 🔼',
                                desc: ' 🔽'
                              }[header.column.getIsSorted()] ?? null}
                            </Typography>
                          </TableCell>
                        ))}
                      </TableRow>
                      {showFilters && (
                        <TableRow>
                          {headerGroup.headers.map((header) => (
                            <TableCell key={header.id}>
                              {header.column.getCanFilter() && showFilters ? (
                                <div>
                                  <Filter column={header.column} />
                                </div>
                              ) : null}
                            </TableCell>
                          ))}
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableHead>
                <TableBody>
                  {loading && (
                    <TableRow>
                      <TableCell colSpan={columns.length} align='center'>
                        <Typography variant='body2' color='text.secondary'>Loading...</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading && data.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={columns.length} align='center'>
                        <Typography variant='body2' color='text.secondary'>No trade partner data available</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {table.getRowModel().rows.map((row) => (
                    <React.Fragment key={row.id}>
                      <TableRow
                        sx={{
                          cursor: 'pointer',
                          transition: (theme) => theme.transitions.create(),
                          '&:hover': {
                            backgroundColor: 'action.hover'
                          }
                        }}
                        onClick={() => handleRowClick(row)}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell
                            key={cell.id}
                            sx={cell.column.columnDef.meta?.style}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Divider />
            <Stack
              gap={1}
              p={3}
              alignItems='center'
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent='space-between'
            >
              <Box display='flex' alignItems='center' gap={1}>
                <Button
                  variant='contained'
                  color='primary'
                  onClick={() => fetchPage()}
                  startIcon={
                    <FontAwesomeIcon
                      icon={faRefresh}
                      style={{ width: 16, height: 16 }}
                      spin={loading}
                    />
                  }
                >
                  Refresh data
                </Button>
                                <Typography variant='body1'>
                  {totalElements} Rows
                </Typography>
              </Box>
              <Box
                sx={{
                  display: {
                    xs: 'block',
                    sm: 'flex'
                  }
                }}
                alignItems='center'
                gap={1}
              >
                                <Stack direction='row' alignItems='center' gap={1}>
                  <Typography variant='body1'>Page</Typography>
                  <Typography variant='body1' fontWeight={600}>
                    {pageIndex + 1} of {totalPages}
                  </Typography>
                </Stack>
                <Stack direction='row' alignItems='center' gap={1}>
                  | Go to page:
                                    <TextField
                    type='number'
                    inputProps={{ min: 1, max: totalPages }}
                    defaultValue={pageIndex + 1}
                                                            onChange={(e) => {
                      const val = Number(e.target.value)
                      if (!val || val < 1) return
                      updateParams({ page: Math.min(val, totalPages) - 1 })
                    }}
                    size='small'
                  />
                </Stack>
                <Select
                  value={pageSize}
                  onChange={(e) => {
                    updateParams({ size: Number(e.target.value), page: 0 })
                  }}
                  size='small'
                >
                  {[5, 10, 15, 20, 25].map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>

                <IconButton
                  size='small'
                  onClick={() => updateParams({ page: 0 })}
                  disabled={pageIndex === 0}
                >
                  <FontAwesomeIcon icon={faAnglesLeft} />
                </IconButton>
                <IconButton
                  size='small'
                  onClick={() => updateParams({ page: pageIndex - 1 })}
                  disabled={pageIndex === 0}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </IconButton>
                <IconButton
                  size='small'
                  onClick={() => updateParams({ page: pageIndex + 1 })}
                  disabled={pageIndex >= totalPages - 1}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </IconButton>
                <IconButton
                  size='small'
                  onClick={() => updateParams({ page: totalPages - 1 })}
                  disabled={pageIndex >= totalPages - 1}
                >
                  <FontAwesomeIcon icon={faAnglesRight} />
                </IconButton>
              </Box>
            </Stack>
          </Stack>
        </ChildCard>
      </Stack>
      <TradePartnerDetailModal
        id={selectedRow?.id}
        open={!!selectedRow}
        onClose={() => setSelectedRow(null)}
        fetchData={() => fetchPage()}
        rowData={selectedRow}
      />
    </LocalizationProvider>
  )
}

export default TradePartnerSubmissions
